import type { DeepPartial } from "@apollo/client/utilities";
import { type ReactNode, useEffect } from "react";

import { TimestampTooltip } from "@/components/shared/timestamp-tooltip";
import type { CreatorStat, TokenMetadatum } from "@/graphql/codegen/graphql";
import { cn } from "@/utils/lib";

import {
  DiscoverCreatorItem,
  DiscoverCreatorItemErrorBoundary,
} from "../../discover/discover-creator-item";
import { CountdownTimerSmall } from "./countdown-timer-small";
import { useTokenMetrics } from "./use-token-metrics";

export const BaseTile = ({
  creator,
  extraContent,
  onClick,
  isSelected,
  className,
  index,
  selectedTokenMetrics,
  selectInitial = true,
  clickable = true,
}: {
  creator: DeepPartial<CreatorStat | TokenMetadatum>;
  extraContent: ReactNode;
  onClick(tokenMetrics: ReturnType<typeof useTokenMetrics>): void;
  isSelected: boolean;
  index: number;
  selectedTokenMetrics: ReturnType<typeof useTokenMetrics> | null;
  className?: string;
  selectInitial?: boolean;
  clickable?: boolean;
}) => {
  const creatorPublicKey = creator.publicKeyBase58Check;
  const tokenMetrics = useTokenMetrics(creatorPublicKey);
  const createdAt = creator.tokenAmmCreatedAt;
  const launchAt = creator.tokenAmmLaunchAt;
  const launchAtDate = new Date(launchAt);

  useEffect(() => {
    if (
      index === 0 &&
      !selectedTokenMetrics &&
      !tokenMetrics.specificCoinLoading &&
      selectInitial
    ) {
      onClick(tokenMetrics);
    }
  }, [index, onClick, selectedTokenMetrics, tokenMetrics, selectInitial]);

  return (
    <DiscoverCreatorItemErrorBoundary key={creator.publicKeyBase58Check}>
      <div
        onClick={() => {
          if (clickable) {
            onClick(tokenMetrics);
          }
        }}
        className={cn(
          "overflow-hidden rounded-2xl border",
          {
            "border border-primary": isSelected,
            "cursor-pointer": clickable,
          },
          className,
        )}
      >
        <div
          className="
            flex w-full flex-col items-center justify-between gap-1
            rounded-t-2xl bg-card p-2 px-3 text-xs
            md:flex-row
          "
        >
          {launchAtDate > new Date() ? (
            <div
              className="
                flex flex-col items-center gap-1
                md:flex-row
              "
            >
              <span className="text-muted">Launches in</span>
              <span className="font-mono text-sm text-red-500">
                <CountdownTimerSmall endingDate={launchAt} />
              </span>
            </div>
          ) : (
            <div className="text-muted">
              <TimestampTooltip timestamp={launchAt} leadingText="Launched" />
            </div>
          )}
          <div className="text-muted">
            <TimestampTooltip timestamp={createdAt} leadingText="Created" />
          </div>
        </div>
        <DiscoverCreatorItem
          user={creator}
          customPrefix="$"
          hasBorder={false}
          extraContent={extraContent}
          isRounded={false}
        />
      </div>
    </DiscoverCreatorItemErrorBoundary>
  );
};
